import React, { createContext, useState } from 'react';
import { getGroundTypeByExploitationId } from 'services/API/Exploitation';
import { displayErrorMessage } from './tools_functions';

export const PdfContext = createContext();

export const ModalContext = React.createContext();

export const LoadingContext = React.createContext({
  fullscreen: false,
  customText: null,
  setFullScreen: (booleanFullscreen) => {},
});

export function LoadingProvider({ children }) {
  const [fullscreen, setFullScreen] = useState(false);
  const [customText, setCustomText] = useState(null);
  const [forceLoader, setForceLoader] = useState(false);
  const [loading, setLoading] = useState(false);

  const resetDataLoader = () => {
    setFullScreen(false);
    setCustomText(null);
    setForceLoader(false);
  };

  return (
    <LoadingContext.Provider
      value={{
        fullscreen,
        setFullScreen,
        customText,
        setCustomText,
        forceLoader,
        setForceLoader,
        loading,
        setLoading,
        resetDataLoader,
      }}
    >
      {children}
    </LoadingContext.Provider>
  );
}

export const ArableContext = React.createContext({
  arableSurface: 0,
  setArableSurface: () => {},
});

export const ExploitationContext = React.createContext({
  exploitationContext: null,
  culturesColors: [],
  setExploitationContext: (context) => {},
  groundTypesList: [],
  getGroundTypesList: () => {},
});

export function ExploitationProvider({ children }) {
  const [exploitationContext, setExploitationContext] = useState(null);
  const [culturesColors, setCulturesColors] = useState([]);
  const [parcelsInformations, setParcelsInformations] = useState([]);
  const [groundTypesList, setGroundTypesList] = useState([]);

  const resetContextExploitations = () => {
    setExploitationContext(null);
  };

  const getGroundTypesList = async () => {
    if (!groundTypesList || !(groundTypesList.length > 0)) {
      const result = await getGroundTypeByExploitationId(localStorage.getItem('exploitation'));
      setGroundTypesList(result.data.data);
    }
  };

  return (
    <ExploitationContext.Provider
      value={{
        exploitationContext,
        setExploitationContext,
        resetContextExploitations,
        culturesColors,
        setCulturesColors,
        parcelsInformations,
        setParcelsInformations,
        groundTypesList,
        getGroundTypesList,
        setGroundTypesList,
      }}
    >
      {children}
    </ExploitationContext.Provider>
  );
}

export const MappingImportResponseContext = createContext();

export function MappingImportResponseProvider({ children }) {
  const [cultureMappingImportResponse, _setCultureMappingImportResponse] = useState({
    skippedLines: [],
    warningInfo: [],
    hasWarning: false,
    mappingType: null,
  });
  const [groundTypeMappingImportResponse, _setGroundTypeMappingImportResponse] = useState({
    skippedLines: [],
    warningInfo: [],
    hasWarning: false,
    mappingType: null,
  });
  const [allMappingData, setAllMappingData] = useState({
    mappingCulture: { lastSelectedMappingType: null, data: null },
    mappingGroundType: { lastSelectedMappingType: null, data: null },
  });

  const cultureMappingData = allMappingData?.mappingCulture?.data;
  const groundTypeMappingData = allMappingData?.mappingGroundType?.data;

  // Warning info from mapping import does not have error code so we create or own error code and parse the string to discriminate
  // by the first word of the string of the error message for each error
  const setCultureMappingImportResponse = (response) => {
    const skippedLines = response.skippedLines;
    const warningInfos = response.warningInfo;

    const translatedSkippedLines = skippedLines.map((skippedLine) => {
      const skippedLineReason =
        skippedLine.reason.split(' ')[0] === 'Duplicate'
          ? 'DUPLICATE_MAPPING'
          : 'MISSING_EXTERNAL_NAME_CULTURE';
      return {
        ...skippedLine,
        reason: displayErrorMessage(skippedLineReason, skippedLine),
      };
    });
    const translatedWarningInfos = warningInfos.map((warningInfo) => {
      const warningInfoReason =
        warningInfo.reason.split(' ')[0] === 'Culture'
          ? 'CULTURE_MAPPING_NOT_FOUND'
          : 'UNKNOWN_MAPPING_ERROR';
      return {
        ...warningInfo,
        reason: displayErrorMessage(warningInfoReason, warningInfo),
      };
    });

    _setCultureMappingImportResponse({
      skippedLines: translatedSkippedLines,
      warningInfo: translatedWarningInfos,
      hasWarning: response.hasWarning,
      mappingType: response.mappingType,
    });
  };

  const setGroundTypeMappingImportResponse = (response) => {
    const skippedLines = response.skippedLines;
    const warningInfos = response.warningInfo;

    const possibleErrors = {
      Duplicate: 'DUPLICATE_MAPPING',
      Missing: 'MISSING_EXTERNAL_NAME_GROUNDTYPE',
      GroundType: 'GROUNDTYPE_NOT_FOUND_MAPPING',
    };

    const translatedSkippedLines = skippedLines.map((skippedLine) => {
      const reason = skippedLine.reason.split(' ')[0];
      let errorReason = 'UNKNOWN_MAPPING_ERROR';
      if (reason in possibleErrors) {
        errorReason = possibleErrors[reason];
      }

      return {
        ...skippedLine,
        reason: displayErrorMessage(errorReason, skippedLine),
      };
    });

    _setGroundTypeMappingImportResponse({
      skippedLines: translatedSkippedLines,
      warningInfo: warningInfos,
      hasWarning: response.hasWarning,
      mappingType: response.mappingType,
    });
  };

  return (
    <MappingImportResponseContext.Provider
      value={{
        cultureMappingImportResponse,
        setCultureMappingImportResponse,
        groundTypeMappingImportResponse,
        setGroundTypeMappingImportResponse,
        cultureMappingData,
        groundTypeMappingData,
        setAllMappingData,
        allMappingData,
      }}
    >
      {children}
    </MappingImportResponseContext.Provider>
  );
}
